import {
  AgentPresenceMetric,
  QueueMetric,
  QueueMetricsPartial,
} from 'lib-dashboard-types';

/** Merges partial metric updates into the existing metrics array. */
export function mergeQueueMetrics(
  existing: readonly QueueMetric[],
  partials: QueueMetricsPartial['metrics'],
): QueueMetric[] {
  return existing.map((metric) => {
    const partial = partials.find((p) => p.type === metric.type);
    if (!partial) return metric;

    if (metric.type === 'agent_presence' && partial.type === 'agent_presence') {
      return mergeAgentPresence(metric, partial);
    }

    return Object.assign({}, metric, partial) as QueueMetric;
  });
}

/**
 * Merges a partial `agent_presence` update into an existing metric. The partial
 * may contain only a subset of agents — those are merged by `agent_id` into the
 * existing agents list rather than replacing the entire array.
 */
function mergeAgentPresence(
  existing: AgentPresenceMetric,
  partial: Partial<AgentPresenceMetric>,
): AgentPresenceMetric {
  const { agents: partialAgents, ...rest } = partial;
  const merged: AgentPresenceMetric = { ...existing, ...rest };

  if (!partialAgents) {
    return merged;
  }

  if (existing.agents) {
    const agentMap = new Map(
      existing.agents.map((agent) => [agent.agent_id, agent]),
    );
    for (const agent of partialAgents) {
      agentMap.set(agent.agent_id, agent);
    }
    return { ...merged, agents: Array.from(agentMap.values()) };
  }

  return { ...merged, agents: [...partialAgents] };
}
